import {
	AxiosError,
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
} from 'axios'
import { waitForLanguage } from 'shared/utils/helpers'
import { logError, logEvent } from 'shared/AppInsight'
import appConfig from 'app.config'

const onRequest = async (
	config: AxiosRequestConfig,
): Promise<AxiosRequestConfig> => {
	config.headers['Accept-Language'] = await waitForLanguage()
	consoleLog(`[request] [${JSON.stringify(config)}]`)
	logEvent('SignInRequest', { config: `${JSON.stringify(config)}` })
	return config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
	let errorMessage = `Error: ${error.message}`

	if (error.request) {
		errorMessage += ' - The request was made but no response was received.'
	} else {
		errorMessage += ' - An error occurred in setting up the request.'
	}

	const errorDetails = {
		message: errorMessage,
		url: error.config?.url,
		method: error.config?.method,
		timestamp: new Date().toUTCString(),
	}
	consoleLog(`[request error] ${errorMessage}`)
	logError(new Error(errorMessage), errorDetails)

	return Promise.reject(error)
}

const onResponse = (response: AxiosResponse): AxiosResponse => {
	consoleLog(`[response] [${JSON.stringify(response)}]`)
	logEvent('SignInResponse', {
		url: response.config.url,
		status: response.status,
		data: response.data,
	})
	return response
}

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
	let errorMessage = `Error: ${error.message}`
	if (error.response?.data.message) {
		errorMessage += ` - Message from server: "${error.response.data.message}"`
	}

	if (!error.response || error.response.status === 0) {
		errorMessage +=
			' - Detected status code 0. Possible causes: network issues, CORS errors, request aborted by the client, or the server did not respond.'

		const errorDetails = {
			message: errorMessage,
			url: error.config.url,
			method: error.config.method,
			headers: JSON.stringify(error.config.headers),
			timestamp: new Date().toUTCString(),
		}

		logError(new Error(errorMessage), errorDetails)
	} else {
		const basicErrorDetails = {
			message: errorMessage,
			status: error.response.status,
			url: error.config.url,
		}

		logError(new Error(errorMessage), basicErrorDetails)
	}

	consoleLog(`[request error] ${errorMessage}`)
	return Promise.reject(error)
}

const logInConsole = appConfig.environment !== 'production'
const consoleLog = (message: string) => {
	if (logInConsole) console.log(message)
}

export function setupInterceptorsFor(instance: AxiosInstance): AxiosInstance {
	instance.interceptors.request.use(onRequest, onRequestError)
	instance.interceptors.response.use(onResponse, onResponseError)
	return instance
}
